import { t } from "i18next";
import { useState } from "react";

import { ControlledSelectValue, SelectOption } from "@shared/form/SelectField/SelectField.types";

import { Group } from "../devices/types";

type ValidationErrors = {
    name?: string;
    description?: string;
    parentId?: string;
};

const getGroupDepth = (groupId: number | null, groups: Group[], depth = 0): number => {
    if (!groupId) {
        return depth;
    }
    const parentGroup = groups.find((group) => group.id === groupId);
    return parentGroup ? getGroupDepth(parentGroup.parentId, groups, depth + 1) : depth;
};

export const validateGroupForm = (
    values: { name: string; description: string; parentId: number | null },
    groups: Group[],
    groupNestingLimit: number,
    currentGroupId?: number,
): ValidationErrors => {
    const errors: ValidationErrors = {};

    const groupNameExists = groups.some(
        (group) => group.id !== currentGroupId && group.name?.toLowerCase().trim() === values.name?.toLowerCase().trim(),
    );

    if (!values.name) {
        errors.name = t("modals:editGroup.input.name.validation.required");
    } else if (groupNameExists) {
        errors.name = t("modals:editGroup.input.name.validation.exists");
    } else if (!Number.isNaN(Number(values.name))) {
        errors.name = t("modals:editGroup.input.name.validation.cannotBeNumber");
    }

    if (values.parentId !== null && values.parentId !== undefined) {
        if (getGroupDepth(values.parentId, groups) >= groupNestingLimit) {
            errors.parentId = t("modals:editGroup.input.parentGroup.validation.limitReached");
        }
    }

    return errors;
};

export const useParentGroupSelection = (groups: Group[], groupNestingLimit: number) => {
    const [isLimitReached, setIsLimitReached] = useState(false);

    const handleParentGroupChange = (value: ControlledSelectValue<false>) => {
        if (typeof value !== "number" && value !== null) {
            setIsLimitReached(false);
            return;
        }

        const depth = getGroupDepth(value, groups);
        setIsLimitReached(depth >= groupNestingLimit);
    };

    return { isLimitReached, handleParentGroupChange };
};

export const expandGroupIdsWithDescendants = (groupIds: number[], allGroups: Group[]): number[] => {
    const validIds = new Set(allGroups.map((g) => g.id));
    const childrenByParent = new Map<number | null, number[]>();
    for (const g of allGroups) {
        const p = g.parentId;
        if (!childrenByParent.has(p)) {
            childrenByParent.set(p, []);
        }
        childrenByParent.get(p)!.push(g.id);
    }
    const result = new Set<number>();
    const addSubtree = (id: number) => {
        if (!validIds.has(id)) {
            return;
        }
        result.add(id);
        for (const childId of childrenByParent.get(id) ?? []) {
            addSubtree(childId);
        }
    };
    for (const id of groupIds) {
        addSubtree(id);
    }
    return Array.from(result);
};

// Returns the subset of `groupIds` that are “roots” within the selection: any selected group whose parent is not
// also selected. Used for **aggregating** workspace statistics (device counts, health, etc.) without double-counting
// when both a parent and its descendants are selected.

// This assumes **parent group statistics already include rolled-up totals for all descendants** (API contract).
// If the backend ever returned direct-only counts per group, aggregation would need a different strategy than
// filtering to these root IDs.

export const getRootGroupIdsInSelection = (groupIds: number[], allGroups: Group[]): number[] => {
    const byId = new Map(allGroups.map((g) => [g.id, g]));
    const selectedSet = new Set(groupIds);

    const hasSelectedStrictAncestor = (groupId: number): boolean => {
        let parentId = byId.get(groupId)?.parentId ?? null;
        while (parentId !== null) {
            if (selectedSet.has(parentId)) {
                return true;
            }
            parentId = byId.get(parentId)?.parentId ?? null;
        }
        return false;
    };

    return groupIds.filter((id) => !hasSelectedStrictAncestor(id));
};

export const getGroupsForAggregation = (selectedGroupIds: number[], allGroups: Group[]): Group[] => {
    const groupIds = selectedGroupIds.length > 0 ? selectedGroupIds : allGroups.map((g) => g.id);
    const rootGroupIds = new Set(getRootGroupIdsInSelection(groupIds, allGroups));
    return allGroups.filter((g) => rootGroupIds.has(g.id));
};

export const getParentGroups = <T extends Group | API.Group>(groupId: number | null, groups: T[]): T[] => {
    const groupsMap = new Map(groups.map((group) => [group.id, group]));
    if (!groupId || !groupsMap.has(groupId)) {
        return [];
    }

    const group = groupsMap.get(groupId);
    if (!group) {
        return [];
    }

    return [group, ...getParentGroups(group.parentId, groups)];
};

export const normalizeGroups = (groups: (API.Group | Group)[]): Group[] => {
    const groupIds: (number | null)[] = groups.map((group) => group.id);

    return groups.map((group) => ({
        ...group,
        parentId: !groupIds.includes(group.parentId) ? null : group.parentId,
    }));
};

// Turns a tree of groups into a flat list, and adds a depth number to each group so we know
// how deep it is nested. Groups with a missing parent, or groups left over (e.g. from a loop
// in parent links) are shown at the top level.
const flattenGroupForest = <T extends { id: number; parentId?: number | null }>(groups: T[]): { group: T; depth: number }[] => {
    const existingGroupIds = new Set(groups.map((group) => group.id));
    const placedGroupIds = new Set<number>();

    const collectChildren = (parentId: number | null, depth: number): { group: T; depth: number }[] =>
        groups
            .filter((group) => {
                if (placedGroupIds.has(group.id)) {
                    return false;
                }

                const groupParentId = group.parentId ?? null;
                const isDirectChild = groupParentId === parentId;
                const isOrphan = groupParentId !== null && !existingGroupIds.has(groupParentId);

                return isDirectChild || (isOrphan && parentId === null);
            })
            .flatMap((group) => {
                placedGroupIds.add(group.id);
                return [{ group, depth }, ...collectChildren(group.id, depth + 1)];
            });

    const flattenedGroups = collectChildren(null, 0);

    groups.forEach((group) => {
        if (placedGroupIds.has(group.id)) {
            return;
        }
        placedGroupIds.add(group.id);
        flattenedGroups.push({ group, depth: 0 }, ...collectChildren(group.id, 1));
    });

    return flattenedGroups;
};

export const getNestedGroupsWithIndentation = (groups: Group[]): SelectOption[] =>
    flattenGroupForest(groups).map(({ group, depth }) => ({
        label: group.name,
        value: group.id,
        depth,
    }));
